/* eslint no-param-reassign: 0 */

import _ from 'underscore';
import Promise from 'bluebird';
import { getODataErrors as legacyGetODataErrors } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import DataStore from '@bingads-webui-component-legacy/js-data-campaign/index';
import edmCampaign from '@bingads-webui-component-legacy/edm-campaign/index';
import BaseRepository from '@bingads-webui-campaign-legacy/adextension-shared/service/repos/base';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/feature/adextension/shared');
const s$i18n = window.globalLegacyI18n.get('$/schema-i18n!campaign');

class BadResponseError {
  constructor(schema) {
    this.type = schema.name;
  }

  toString() {
    return i18n.get('AdExtensionEditor_FailedToSave', {
      type: s$i18n.type(this.type),
    });
  }
}

const isCacheValid = {};

export default class ExtensionsRepository extends BaseRepository {
  constructor(options) {
    super(options);
    this._schema = options.schema;
    this._store = DataStore[this._schema.name];
    this.dataFormatter = options.extensionDataFormatter;
    this.afterChangeComitted = options.afterChangeComitted;
    this.skipExtensionCache = options.skipExtensionCache;
    this.getODataErrors = options.getODataErrors || legacyGetODataErrors;

    if (!_.has(isCacheValid, this._schema.name)) {
      isCacheValid[this._schema.name] = false;
    }
  }

  storeOptions() {
    return {
      entitySet: edmCampaign.currentAccount.AdExtensions,
      cacheResponse: false,
    };
  }

  isNew(entity) {
    return _.isEmpty(entity) || _.isUndefined(entity[this._schema.key]);
  }

  create(params) {
    let isUpdating = !_.isUndefined(params.Id);
    let schema = this._schema;
    let key = this._schema.key;

    return this._store.create(params, this.storeOptions())
      .catch((response) => {
        if (response && !_.isEmpty(response.errors)) {
          throw this.getODataErrors(response.errors);
        }

        throw new BadResponseError(schema);
      })
      .then((data) => {
        isCacheValid[this._schema.name] = false;

        if (isUpdating && data.value === 0) {
          return params;
        } else if (!isUpdating && _.isNumber(data.value) && data.value > 0) {
          let ret = _.clone(params);

          ret[key] = data.value;

          return ret;
        }

        throw new BadResponseError(schema);
      })
      .tap(this.afterChangeComitted);
  }

  find(id) {
    return this._store.find(Number(id), this.storeOptions());
  }

  findAll(params) {
    let formatter = this.dataFormatter;
    let options = this.storeOptions();

    _.extend(options, { bypassCache: true });

    params = this.adaptParams(params);

    if (!isCacheValid[this._schema.name] || this.skipExtensionCache) {
      options.headers = _.extend(options.headers || {},
        {'x-ms-refreshcampaigncache': 'true'});
    }

    return this._store
      .findAll(params, options)
      .then((data) => {
        isCacheValid[this._schema.name] = true;
        // if response is with errors, then throw.
        if (data.status >= 400) {
          const errorResult = {
            errors: [data],
            type: 'odata',
          };

          throw errorResult;
        }
        return Promise.props({
          items: formatter(data),
          total: data.totalCount,
        });
      });
  }

  destroy(id) {
    return this._store
      .destroy(Number(id), this.storeOptions())
      .then((data) => {
        isCacheValid[this._schema.name] = false;
        return data;
      })
      .tap(this.afterChangeComitted);
  }

  update(params) {
    let schema = this._schema;

    return this._store.update(params.Id, params, this.storeOptions())
      .then((data) => {
        isCacheValid[this._schema.name] = false;

        if (data.value === 0) {
          return params;
        }

        throw new BadResponseError(schema);
      })
      .tap(this.afterChangeComitted);
  }
}

ExtensionsRepository.prototype.BadResponseError = BadResponseError;
